import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { datadogRum } from '@wehunt/core-web'
import { ReplayBundleView } from './ReplayBundleView'
import { ReplayStatusScreen } from './ReplayStatusScreen'
import { chatDtoToMessages } from './chatDtoToMessages'
import { replayDtoToBundle } from './replayDtoToBundle'
import { useHuntReplay } from './useHuntReplay'
import { useHuntReplayChat } from './useHuntReplayChat'

/**
 * The replay destination: `/replay/$huntId`.
 *
 * Campfire owns the whole path namespace rather than nesting under clubhouse's
 * `/hunts/$id`, so the two domains keep separate path space and a share link has a
 * route of its own to land on.
 *
 * `from` and `teamName` ride the query string. `from` is where Close returns to —
 * the hunt detail page for a signed-in viewer, and the hunt list for someone who
 * arrived on a link with nowhere else to go back to.
 */
/**
 * The chat call's outcome as one word, so a dashboard can group on it without
 * anyone having to remember which status means what.
 */
function chatOutcome(status: number | undefined): string {
  if (status === undefined) return 'ok'
  if (status === 403) return 'refused'
  if (status === 404) return 'absent'
  return 'unavailable'
}

export function ReplayRoute() {
  const { t } = useTranslation('replay')
  const navigate = useNavigate()
  const { huntId } = useParams({ strict: false }) as { huntId: string }
  const location = useLocation()

  const params = new URLSearchParams(location.search)
  const from = params.get('from') ?? '/hunts'
  const teamName = params.get('teamName') ?? undefined
  const source = params.get('source') ?? 'direct'

  const { data, isLoading, error, refetch } = useHuntReplay(huntId)

  // Reported once per arrival, with where the viewer came from, so the share funnel
  // can tell a link open apart from a click inside the app.
  useEffect(() => {
    datadogRum.addAction('replay_viewed', { huntId, source })
  }, [huntId, source])

  const close = () => {
    const url = new URL(from, window.location.origin)
    const search = Object.fromEntries(url.searchParams.entries())
    navigate({
      to: url.pathname as '/',
      search: Object.keys(search).length > 0 ? search : undefined,
    })
  }

  const archive = useMemo(
    () => (data === undefined ? undefined : replayDtoToBundle(data, { teamName })),
    [data, teamName],
  )

  // Asked for only once the archive is in hand: the chat is anchored to the
  // archive's participants, so there is nothing to do with it before then, and a
  // replay that turns out not to exist should not have gone looking for its chat.
  const {
    data: chat,
    error: chatError,
    isPending: chatPending,
  } = useHuntReplayChat(huntId, archive !== undefined)

  // Chat is deliberately not allowed to gate this. The two are separate requests
  // so that a viewer who may not read the chat — or a chat service having a bad
  // day — still gets the whole hunt; `messages` stays absent and the viewer
  // simply draws no bubbles.
  const bundle = useMemo(() => {
    if (archive === undefined) return undefined
    if (chat === undefined) return archive
    return { ...archive, messages: chatDtoToMessages(chat, archive.participants) }
  }, [archive, chat])

  // Reported once the chat call has settled, because from outside every one of
  // these looks identical: the replay draws and there are no bubbles. Without
  // it, "the chat is broken" is unanswerable — we cannot tell a refusal from a
  // hunt where nobody spoke, and that is exactly the hole that made the replay
  // failures take days to diagnose.
  //
  // `received` against `shown` is the pair that matters. A hunt with messages
  // that draws none is the case a user will report, and it is not a fault: the
  // senders had no trail to anchor a bubble to.
  useEffect(() => {
    if (archive === undefined || chatPending) return
    datadogRum.addAction('replay_chat_loaded', {
      huntId,
      outcome: chatOutcome(chatError?.status),
      status: chatError?.status,
      received: chat?.messages?.length,
      shown: bundle?.messages?.length,
    })
  }, [archive, chatPending, chatError, chat, bundle, huntId])

  if (isLoading) {
    return <ReplayStatusScreen title={t('status.loading')} onClose={close} />
  }

  if (error !== null) {
    // 404 is final — this hunt has no archive, so there is nothing to retry.
    const notFound = error.status === 404
    return (
      <ReplayStatusScreen
        title={notFound ? t('status.notFoundTitle') : t('status.unavailableTitle')}
        body={notFound ? t('status.notFoundBody') : t('status.unavailableBody')}
        onClose={close}
        onRetry={notFound ? undefined : () => void refetch()}
      />
    )
  }

  if (bundle === undefined) {
    return <ReplayStatusScreen title={t('status.unavailableTitle')} onClose={close} />
  }

  return <ReplayBundleView bundle={bundle} onClose={close} onRetry={() => void refetch()} />
}
